import React, { useEffect, useState } from 'react';
import { RootState } from 'src/store/reducers/state';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalyticsActions } from 'src/store/analytics/actions';
import Loader from 'src/components/loader';
import { defaultDateFormatter } from 'src/components/utilities/utils';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Pagination from 'src/components/common/pagination';
import { DebounceInput } from 'react-debounce-input';
import { formatDate } from 'src/components/utilities/utils';
import { getCourseStatus } from 'src/components/utilities/utils';
import * as XLSX from 'xlsx';
import DownloadModel from 'src/components/common/DownloadModel';
import axios from 'axios';
import MemberCourseDetail from '../components/MemberCourseDetail';

const SingleAnalyticsCourse: React.FC = () => {
  const dispatch = useDispatch();
  const [tab, setTab] = useState('analytics');
  const analyticsAction = useAnalyticsActions(dispatch);
  const [selectedMember, setSelectedMember] = useState<{ id: string; name: string } | null>(null);
  const { id } = useParams<string>();
  const navigate = useNavigate()
  const {
    viewActivity,
    singleCourseResult,
    isSingleCourseResultLoading,
    recentGrade,
    courseResult,
    singleCourseResultPaginationMeta,
    singleRecentCourseActivity,
    singleRecentCourseActivityPaginationMeta,
    isRecentActivityLoading
  } = useSelector((state: RootState) => {
    return {
      viewActivity: state.analytics.viewActivity,
      singleCourseResult: state.analytics.singleCourseResult,
      isSingleCourseResultLoading: state.analytics.isSingleCourseResultLoading,
      recentGrade: state.analytics.recentGrade,
      courseResult: state.analytics.courseResult,
      singleCourseResultPaginationMeta: state.analytics.singleCourseResultPaginationMeta,
      singleRecentCourseActivity: state.analytics.singleRecentCourseActivity,
      singleRecentCourseActivityPaginationMeta:
        state.analytics.singleRecentCourseActivityPaginationMeta,
      isRecentActivityLoading: state.analytics.isRecentActivityLoading
    };
  });
  const [searchValue, setSearchValue] = useState<string>('');
  const [barChartData, setBarChartData] = useState({
    labels: recentGrade && recentGrade.map((data) => data.month),
    datasets: [
      {
        label: 'Total Range',
        data: recentGrade && recentGrade.map((data) => data.range),
        backgroundColor: ['#2A71d0']
      }
    ]
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  useEffect(() => {
    setBarChartData({
      labels: recentGrade && recentGrade.map((data) => data.month),
      datasets: [
        {
          label: 'Total Range',
          data: recentGrade && recentGrade.map((data) => data.range),
          backgroundColor: ['#82B5D0']
        },
        {
          label: '25th-75th Percentile',
          data: recentGrade && recentGrade.map((data) => data.percentile),
          backgroundColor: ['#1A92FF']
        },
        {
          label: 'Median',
          data: recentGrade && recentGrade.map((data) => data.median),
          backgroundColor: ['#ADD8E6']
        }
      ]
    });
  }, [recentGrade]);

  useEffect(() => {
    if (id) {
      analyticsAction.getSingleCourseResultRequest({
        id,
        querySearch: '',
        pageNumber: 1,
        pageSize: 10
      });
    }
    analyticsAction.getRecentGradeRequest();
  }, [id]);

  const handlePageChange = (pageNumber: number) => {
    analyticsAction.getSingleCourseResultRequest({
      id,
      querySearch: searchValue,
      pageNumber,
      pageSize: 10
    });
  };

  const handleSingleRecentPageChange = (pageNumber: number) => {
    analyticsAction.getSingleRecentCourseActivityRequest({
      id,
      querySearch: searchValue,
      pageNumber,
      pageSize: 10
    });
  };

  const handleSearchCourse = (e: string) => {
    setSearchValue(e);
    analyticsAction.getSingleCourseResultRequest({
      id,
      querySearch: e,
      pageNumber: 1,
      pageSize: 10
    });
  };

  const handleConfirmDownload = async () => {
    setIsDownloading(true);
    setDownloadProgress(0);
    let allData: any[] = [];
    let pageNumber = 1;
    const pageSize = 20;
    let totalPages = 1;
    const rootUrl = process.env.REACT_APP_BASE_API_URL;
    const bearerToken = localStorage.getItem('token');

    if (!bearerToken) {
      console.error('No token found in localStorage');
      setIsDownloading(false);
      return;
    }

    try {
      // Fetch first page to get total pages
      const apiPath = '/v2/analytics/member/course';
      const apiUrl = `${rootUrl}${apiPath}?courseid=${id}&pageNumber=${pageNumber}&pageSize=${pageSize}`;
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
          'Content-Type': 'application/json'
        }
      });
      allData = [...response.data.result];
      totalPages = Math.ceil(response.data.totalCount / pageSize);
      setDownloadProgress((1 / totalPages) * 100);

      // Fetch remaining pages
      for (pageNumber = 2; pageNumber <= totalPages; pageNumber++) {
        const nextUrl = `${rootUrl}${apiPath}?courseid=${id}&pageNumber=${pageNumber}&pageSize=${pageSize}`;
        const nextResponse = await axios.get(nextUrl, {
          headers: {
            Authorization: `Bearer ${bearerToken}`,
            'Content-Type': 'application/json'
          }
        });
        allData = [...allData, ...nextResponse.data.result];
        setDownloadProgress((pageNumber / totalPages) * 100);
      }

      if (allData.length === 0) {
        console.warn('No data fetched for download');
        return;
      }

      // Format data for download
      const csvData = allData.map((item) => [
        item.userName || '',
        item.courseCompletionPercentage || 0,
        item.couseStatus || '',
        item.creditsAvailable || 0,
        item.creditsAchieved || 0,
        item.courseCreditTypes || '',
        item.creditsSent || false,
        item.score || 0,
        item.pointsPossible || 0,
        item.pointsEarned || 0,
        item.totalVideos || 0,
        item.totalVideosAttended || 0,
        item.totalQuestions || 0,
        item.totalAnswered || 0,
        item.totalCorrectAnswered || 0,
        item.completionDate ? formatDate(item.completionDate) : '',
        item.submittedDate ? formatDate(item.submittedDate) : ''
      ]);

      const finalDownloadData = [
        ['Course Name:', allData[0]?.courseName || ''],
        [''],
        [
          'Member',
          'Pass Rate',
          'Status',
          'Credits Available',
          'Credits Achieved',
          'Credit Type',
          'Credits Sent',
          'Score %',
          'Total Points',
          'Total Points Earned',
          'Total Videos',
          'Total Videos Attended',
          'Total Questions',
          'Total Answered',
          'Total Correct',
          'Completion Date',
          'Submitted Date'
        ],
        ...csvData
      ];

      // Generate CSV using SheetJS
      const ws = XLSX.utils.aoa_to_sheet(finalDownloadData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'CourseAnalytics');
      const csvContent = XLSX.write(wb, { bookType: 'csv', type: 'string' });
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'CourseAnalytics.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  useEffect(() => {
    analyticsAction.getCourseResultsRequest({ id, pageNumber: 1, pageSize: 25 });
  }, []);

  const handleCourseActivitySearch = (value: string) => {
    analyticsAction.getSingleRecentCourseActivityRequest({
      id,
      querySearch: value,
      pageNumber: 1,
      pageSize: 10
    });
  };
  return (
    <>
      <div className="dashboard">
        <Link to={`/analytic/course`}>
          <i className="fas fa-arrow-left mt-2 mb-2"></i>
          <span className="ml-2">Go Back</span>
        </Link>
        <div className="flex">
          <h2 className="font-semibold ">Course Analytics</h2>
        </div>
        <div className="mt-4 members-list">
          {/* <div className={`dashboard-card my-5 `}>
            <BarChart chartData={barChartData} />
          </div> */}
          <div className="flex flex-wrap mt-5 dashboard-tiles">
            <div className="pr-5 xl:w-1/5 lg:w-1/2 md:w-1/2 sm:w-full w-full mb-5">
              <div className="dashboard-card flex-items justify-center">
                <div className="flex-items w-full p-4">
                  <div className="flex flex-col">
                    <span className="text-2xl font-semibold">
                      {courseResult && courseResult[0]?.totalCompleted}
                    </span>
                    <span className="mb-2">Completed</span>
                  </div>
                  <div>
                    <div className="rounded-full">
                      <div
                        className={`w-10 h-10 flex items-center justify-center bg-light-2 rounded-full`}>
                        <i className="fa fa-check-square-o"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pr-5 xl:w-1/5 lg:w-1/2 md:w-1/2 sm:w-full w-full mb-5">
              <div className="dashboard-card flex-items justify-center">
                <div className="flex-items w-full p-4">
                  <div className="flex flex-col">
                    <span className="text-2xl font-semibold">
                      {' '}
                      {courseResult && courseResult[0]?.totalFailed}
                    </span>
                    <span className="mb-2">Failed</span>
                  </div>
                  <div>
                    <div className="rounded-full">
                      <div
                        className={`w-10 h-10 flex items-center justify-center bg-light-1 rounded-full`}>
                        <i className="fa fa-question-circle"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="pr-5 xl:w-1/5 lg:w-1/2 md:w-1/2 sm:w-full w-full mb-5">
              <div className="dashboard-card flex-items justify-center">
                <div className="flex-items w-full p-4">
                  <div className="flex flex-col">
                    <span className="text-2xl font-semibold">
                      {' '}
                      {courseResult && courseResult[0]?.totalInProgress}
                    </span>
                    <span className="mb-2">In Progress</span>
                  </div>
                  <div>
                    <div className="rounded-full">
                      <div
                        className={`w-10 h-10 flex items-center justify-center bg-light-5 rounded-full`}>
                        <i className="fa fa-check"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="pr-5 xl:w-1/5 lg:w-1/2 md:w-1/2 sm:w-full w-full mb-5">
              <div className="dashboard-card flex-items justify-center">
                <div className="flex-items w-full p-4">
                  <div className="flex flex-col">
                    <span className="text-2xl font-semibold">
                      {' '}
                      {courseResult && courseResult[0]?.averageScore}
                    </span>
                    <span className="mb-2">Average Score</span>
                  </div>
                  <div>
                    <div className="rounded-full">
                      <div
                        className={`w-10 h-10 flex items-center justify-center bg-light-3 rounded-full`}>
                        <i className="fa fa-tachometer-average" aria-hidden="true"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pr-5 xl:w-1/5 lg:w-1/2 md:w-1/2 sm:w-full w-full mb-5">
              <div className="dashboard-card flex-items justify-center">
                <div className="flex-items w-full p-4">
                  <div className="flex flex-col">
                    <span className="text-2xl font-semibold">
                      {' '}
                      {courseResult && courseResult[0]?.highestScore}
                    </span>
                    <span className="mb-2">Highest Score</span>
                  </div>
                  <div>
                    <div className="rounded-full">
                      <div
                        className={`w-10 h-10 flex items-center justify-center bg-light-4 rounded-full`}>
                        <i className="fa fa-star"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ul className="flex flex-wrap text-sm font-medium text-center text-gray-500 dark:text-gray-400 cursor-pointer">
            <li className="me-2" onClick={() => setTab('analytics')}>
              <span
                className={`font-extrabold inline-block px-4 py-3 rounded-lg ${tab == 'analytics'
                  ? 'bg-purple-600 text-white'
                  : ' hover:text-gray-900 hover:bg-gray-100'
                  }`}>
                Analytics
              </span>
            </li>
            <li
              className="me-2"
              onClick={() => {
                setTab('activity');
                analyticsAction.getSingleRecentCourseActivityRequest({
                  id,
                  pageNumber: 1,
                  pageSize: 10
                });
              }}>
              <span
                className={`font-extrabold inline-block px-4 py-3 rounded-lg   ${tab == 'activity'
                  ? 'bg-purple-600 text-white'
                  : 'hover:text-gray-900 hover:bg-gray-100'
                  }`}>
                Recent Course Activity
              </span>
            </li>
          </ul>
          {tab == 'analytics' && (
            <div className="mt-5">
              <div className=" py-4 border-b border-gray-100 md:flex justify-between items-center">
                <h2 className="font-semibold ">
                  {singleCourseResult && singleCourseResult.length
                    ? `${singleCourseResult && singleCourseResult[0]?.courseName}`
                    : ''}
                </h2>
                <div className="md:flex items-center text-center">
                  <div className="form-group md:flex items-center text-center">
                    <label className="mr-1 font-semibold">Search:</label>
                    <DebounceInput
                      placeholder="Search"
                      minLength={1}
                      className="input-style w-48 mr-3 mb-1"
                      debounceTimeout={500}
                      onChange={(event) => handleSearchCourse(event.target.value)}
                    />
                  </div>
                  {!!singleCourseResult.length && (
                    <button
                      className="btn-primary w-auto mt-0 mr-0 ml-auto borderonly-btn"
                      data-bs-toggle="modal"
                      data-bs-target="#downloadModel">
                      Download
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-col justify-center h-full">
                <div className="w-full mx-auto bg-white shadow-card rounded-lg relative">
                  <div className="p-3">
                    <div className="overflow-x-auto">
                      <table className="table-auto w-full border">
                        <thead className="text-xs font-semibold uppercase bg-gray-50">
                          <tr>
                            <th className="p-2">
                              <div className="font-semibold text-left">Member</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Pass Rate</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Status</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Credits Available</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Credits Achieved</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Credit Type</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Credits Sent</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Score %</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Total Points</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Total Points Earned</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Total Videos</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Total Videos Attended</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Total Questions</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Total Answered</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Total Correct</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Completion Date</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Submitted Date</div>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="text-sm divide-y divide-gray-100">
                          {isSingleCourseResultLoading ? (
                            <tr>
                              <td>
                                <Loader />
                              </td>
                            </tr>
                          ) : (
                            <>
                              {singleCourseResult && singleCourseResult.length ? (
                                <>
                                  {singleCourseResult &&
                                    singleCourseResult.map((item) => {
                                      return (
                                        <tr key={item.id}
                                          className="cursor-pointer hover:bg-purple-50 transition-colors"
                                          onClick={() => {
                                            setSelectedMember({ id: item.memberId, name: item.userName })
                                            navigate(`/memberCourseDetail/${item.courseId}/${item.memberId}`)
                                          }}
                                        >
                                          <td className="p-2">
                                            <div className="text-left">{item.userName}</div>
                                          </td>
                                          <td className="p-2">
                                            <div className="text-left">
                                              {item.courseCompletionPercentage}%
                                            </div>
                                          </td>
                                          <td className="p-2">
                                            <div className="text-left">
                                              <span
                                                className={`${getCourseStatus(
                                                  item.couseStatus
                                                )} text-xs font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-yellow-900 dark:text-yellow-300 single-line-badge`}>
                                                {item.couseStatus}
                                              </span>
                                            </div>
                                          </td>
                                          <td className="p-2">
                                            <div className="text-left">{item.creditsAvailable}</div>
                                          </td>
                                          <td className="p-2">
                                            <div className="text-left">{item.creditsAchieved}</div>
                                          </td>
                                          <td className="p-2">
                                            <div className="text-left">
                                              {item.courseCreditTypes}
                                            </div>
                                          </td>
                                          <td className="p-2">
                                            <div className="text-left">
                                              {item.creditsSent ? (
                                                <i className="fa fa-check text-danger text-lg text-green-500"></i>
                                              ) : (
                                                ''
                                              )}
                                            </div>
                                          </td>
                                          <td className="p-2">
                                            <div className="text-left">{item.score}</div>
                                          </td>
                                          <td className="p-2">
                                            <div className="text-left">{item.pointsPossible}</div>
                                          </td>
                                          <td className="p-2">
                                            <div className="text-left">{item.pointsEarned}</div>
                                          </td>
                                          <td className="p-2">
                                            <div className="text-left">{item.totalVideos}</div>
                                          </td>
                                          <td className="p-2">
                                            <div className="text-left">
                                              {item.totalVideosAttended}
                                            </div>
                                          </td>
                                          <td className="p-2">
                                            <div className="text-left">{item.totalQuestions}</div>
                                          </td>
                                          <td className="p-2">
                                            <div className="text-left">{item.totalAnswered}</div>
                                          </td>
                                          <td className="p-2">
                                            <div className="text-left">
                                              {item.totalCorrectAnswered}
                                            </div>
                                          </td>
                                          <td className="p-2">
                                            <div className="text-left">
                                              {item.completionDate
                                                ? formatDate(item.completionDate)
                                                : ''}
                                            </div>
                                          </td>
                                          <td className="p-2">
                                            <div className="text-left">
                                              {item.submittedDate
                                                ? formatDate(item.submittedDate)
                                                : ''}
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                </>
                              ) : (
                                <tr>
                                  <td>No Data Found</td>
                                </tr>
                              )}
                            </>
                          )}
                        </tbody>
                      </table>

                      {!!singleCourseResultPaginationMeta &&
                        !!Object.keys(singleCourseResultPaginationMeta).length && (
                          <Pagination
                            currentPage={singleCourseResultPaginationMeta.pageNumber}
                            totalCount={singleCourseResultPaginationMeta.totalCount}
                            pageSize={singleCourseResultPaginationMeta.pageSize}
                            onPageChange={(page: any) => handlePageChange(page)}
                          />
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {tab == 'activity' && (
            <div className="mt-5">
              <div className=" py-4 border-b border-gray-100 md:flex justify-between items-center">
                <h2 className="font-semibold ">Recent Course Activity</h2>
                <div className="md:flex items-center text-center">
                  <div className="form-group md:flex items-center text-center">
                    <label className="mr-1 font-semibold">Search:</label>
                    <DebounceInput
                      placeholder="Search"
                      minLength={1}
                      className="input-style w-48 mr-3 mb-1"
                      debounceTimeout={500}
                      onChange={(event) => handleCourseActivitySearch(event.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col justify-center h-full">
                <div className="w-full mx-auto bg-white shadow-card rounded-lg relative">
                  <div className="p-3">
                    <div className="overflow-x-auto">
                      <table className="table-auto w-full border">
                        <thead className="text-xs font-semibold uppercase bg-gray-50">
                          <tr>
                            <th className="p-2">
                              <div className="font-semibold text-left">Email</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">User Name</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Course</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Video</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Channel</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Quiz Name</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Submitted Date</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Pass Rate</div>
                            </th>
                            <th className="p-2">
                              <div className="font-semibold text-left">Score</div>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="text-sm divide-y divide-gray-100">
                          {isRecentActivityLoading ? (
                            <tr>
                              <td>
                                <Loader />
                              </td>
                            </tr>
                          ) : (
                            <>
                              {singleRecentCourseActivity &&
                                singleRecentCourseActivity.map((item) => {
                                  return (
                                    <tr key={item.courseId}>
                                      <td className="p-2">
                                        <div className="text-left">{item.email}</div>
                                      </td>
                                      <td className="p-2">
                                        <div className="text-left">{item.username}</div>
                                      </td>
                                      <td className="p-2">
                                        <div className="text-left">{item.course}</div>
                                      </td>
                                      <td className="p-2">
                                        <div className="text-left">{item.video}</div>
                                      </td>
                                      <td className="p-2">
                                        <div className="text-left">{item.channel}</div>
                                      </td>
                                      <td className="p-2">
                                        <div className="text-left">{item.quizname}</div>
                                      </td>
                                      <td className="p-2">
                                        <div className="text-left">
                                          {item.submittedDate ? item.submittedDate : ''}
                                        </div>
                                      </td>
                                      <td className="p-2">
                                        <div className="text-left">{item.passrate}</div>
                                      </td>
                                      <td className="p-2">
                                        <div className="text-left">{item.score}</div>
                                      </td>
                                    </tr>
                                  );
                                })}
                            </>
                          )}
                        </tbody>
                      </table>
                      {!!singleRecentCourseActivityPaginationMeta &&
                        !!Object.keys(singleRecentCourseActivityPaginationMeta).length && (
                          <Pagination
                            currentPage={singleRecentCourseActivityPaginationMeta.pageNumber}
                            totalCount={singleRecentCourseActivityPaginationMeta.totalCount}
                            pageSize={singleRecentCourseActivityPaginationMeta.pageSize}
                            onPageChange={(page: any) => handleSingleRecentPageChange(page)}
                          />
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {singleCourseResult && singleCourseResult.length > 0 && (
        <DownloadModel
          isDownloading={isDownloading}
          downloadProgress={downloadProgress}
          handleConfirmDownload={handleConfirmDownload}
        />
      )}
      {/* {selectedMember && id && (
        <MemberCourseDetail
          courseId={id}
          memberId={selectedMember.id}
          memberName={selectedMember.name}
          onBack={() => setSelectedMember(null)}
        />
      )} */}
    </>
  );
};

export default SingleAnalyticsCourse;
